/**
 * КАРТОЧКА ОТВЕТА — единственный владелец состояния поля на экране (UX_SPEC §3.1, §11.2).
 * О количестве снимков она не говорит ни слова: знаменатель на экране один, и печатает его
 * блок снимков (§3.5).
 *
 * Четыре слота, одинаковых по роли у всех состояний (правила текстов — ТЗ.md):
 *
 *   1. заголовок ПАРЫ «состояние × тип поля» — пять на шесть внутренних состояний;
 *   2. факт: что случилось с ПОЛЕМ, с датами. У «Нет чётких снимков» его нет — там факт и
 *      есть причина, и печатается она один раз (правило одного отрицания, §3.2);
 *   3. ОГОВОРКА — цена заголовка. Видна ВСЕГДА, не в раскрытии: раскрытие можно не открыть,
 *      а заголовок прочитан уже всеми;
 *   4. строка, которая делает факт утверждением: сухость / оговорка радара / «что было после
 *      этой даты» / приглашение вернуться.
 *
 * ЛИНЕЙКА СЛЕВА — СВЕТОФОР, а не имя состояния (решение владельца, 2026-09-04): зелёная —
 * копать можно, кирпичная — нельзя, стернёвая — между, серая — ответа нет. Тон берётся от
 * `verdict.tone`: заголовок выбирает пара «состояние × тип поля», а внутри разрешающих
 * заголовков цвет решает ПОВЕРХНОСТЬ поля — по стерне не копают, копают по открытой почве.
 * Цветом дело не ограничивается: у «между» и у молчания линейка пунктирная, у разрешения и
 * запрета сплошная. Кегль различителем не был и не станет — не-ответ не крупнее ответа.
 *
 * ОБЪЯСНЯЕТ ЦВЕТ ДАТИРОВАННАЯ СТРОКА ПОВЕРХНОСТИ, и живёт она в блоке доказательства
 * («На снимке 22 сентября — открытая почва»), видимая без «Чисел». Карточка повторяет её
 * только в одном случае — когда зоны нет вовсе и повторять нечего (`verdict.surfaceLine`).
 *
 * ЧИСЛА БАЛЛА ЗДЕСЬ НЕТ НИ У ОДНОГО СОСТОЯНИЯ. Оно равно шагу сигнала, делённому на его же
 * порог: напечатать его значит напечатать порог. Число живёт в служебной панели.
 */

import { T } from "../strings";
import { formatDayMonth } from "../lib/format";
import type { Verdict as VerdictModel } from "../lib/verdict";

interface Props {
  verdict: VerdictModel;
  revealKey?: number;
  /**
   * ОГОВОРКА «Что это значит» — закрытый список причин, между которыми снимок не выбирает.
   * Считается снаружи: она зависит от ТИПА ПОЛЯ (ячейка 1), а не от прогона.
   */
  caveat?: string | null;
  /**
   * «Числа» ИЛИ служебный режим. Закрывает раскрытие «что видно на снимках», но не
   * оговорку: она видна всегда. Раскрытие «Насколько точен ответ» (`worth*`) УДАЛЕНО
   * 2026-09-05 (закон зачистки, (правила текстов — ТЗ.md): продукт о своей точности не
   * говорит нигде — счёт попаданий и выборки живут в docs.
   */
  showNumbers?: boolean;
  /**
   * Дата снимка, который состояние велит посмотреть глазами, и как его показать. Состояние,
   * определённое через действие пользователя, обязано давать способ это действие совершить.
   */
  previewDate?: string | null;
  onShowDate?: (date: string) => void;
}

/**
 * ДЛИНА, ПОСЛЕ КОТОРОЙ ЗАГОЛОВОК ПЕРЕСТАЁТ УКЛАДЫВАТЬСЯ В ДВЕ СТРОКИ.
 *
 * Три заголовка словаря длиннее сорока знаков — «На поле посеяна культура (копать запрещено)»
 * (43), «Поле под многолетними травами (копать запрещено)» (48) и «На поле произведена
 * обработка почвы (пахота, дискование)» (56), — и на съёмке QA второй занял ТРИ строки. Три
 * строки в шапке карточки сдвигают вниз всё, ради чего её открыли, и читаются они уже не как
 * ответ, а как абзац.
 *
 * Метка, а не расчёт в CSS: ступень кегля выбирается по длине СТРОКИ, а `clamp` умеет считать
 * только от ширины контейнера. Порог 40 стоит между 35 («На поле произведена уборка (стерня)»,
 * который в две строки укладывается и на полном кегле) и 43 — то есть ровно там, где словарь
 * сам его и оставил. Арифметика подгонки — в `.k-verdict__state[data-long]` (layout.css).
 */
const LONG_HEADING_CHARS = 40;

/**
 * Строке 4 нужен свой testid у состояний, которые тесты различают по ней. У «Нет чётких
 * снимков» её нет вовсе — приглашение вернуться стоит в своём слоте (`verdict-comeback`).
 */
const SECOND_LINE_TESTID: Partial<Record<VerdictModel["kind"], string>> = {
  free: "verdict-dry",
  radar: "verdict-radar",
  maybe: "verdict-maybe",
  crop: "verdict-crop-caption",
  fallow: "verdict-fallow-evidence",
};

export function VerdictBlock({
  verdict,
  revealKey = 0,
  caveat = null,
  showNumbers = false,
  previewDate,
  onShowDate,
}: Props) {
  const showImage = verdict.needsEyes && previewDate && onShowDate ? previewDate : null;
  // Правило одного отрицания: у шестого состояния и у упавшего прогона про ОТСУТСТВИЕ
  // говорит одна строка — причина, — и строки 4 там нет (§3.2). Приглашение вернуться под
  // правило не подпадает и потому живёт в собственном слоте: оно говорит не о том, чего
  // нет, а о том, когда смотреть снова.
  const silent = verdict.kind === "nodata";
  // Раскрытие держит наблюдение и характер схода: у пара своего `measured` больше нет
  // (перечисление режимов отказа — видимая оговорка `caveat.fallow`), а `dropLine` с
  // 2026-09-05 живёт здесь, не строкой карточки (drop-lines-off-card). `fallowEvidence`
  // УДАЛЁН тем же днём (закон зачистки, §16): пересказ границы Уилсона — внутренняя
  // методика; счёт чистых снимков окна печатает `fallowSpan` строкой 4.
  const hasDisclosure = Boolean(verdict.measured || verdict.dropLine);

  return (
    <section
      className={`k-verdict k-verdict--${verdict.tone} k-reveal`}
      key={revealKey}
      style={{ ["--verdict-color" as string]: verdict.colorVar }}
      aria-live="polite"
    >
      <h2
        className="k-block__title k-verdict__state"
        data-testid="verdict-state"
        data-long={verdict.label.length > LONG_HEADING_CHARS ? "" : undefined}
      >
        {verdict.label}
      </h2>

      {/* Строки на месте балла БОЛЬШЕ НЕТ (2026-09-05, drop-lines-off-card): характер схода
          уехал в раскрытие «Что видно на снимках», а семейство `changeUnknown*` удалено как
          дубли. Раскрытия «Насколько точен ответ» (`worth*`) НЕТ тем же днём (закон
          зачистки, §16): о точности продукт не говорит ни на одном ярусе. */}

      {verdict.human && (
        <p className="k-verdict__human" data-testid="verdict-human">
          {verdict.human}
        </p>
      )}

      {/* ПОВЕРХНОСТЬ — на карточке только тогда, когда её не прочитали вовсе. Тон схода взят
          от зоны последнего чистого снимка, и называет её словом полоска уровня в блоке
          доказательства, без «Чисел». Зоны нет — полоски нет, и «между» осталось бы цветом без
          строки; поэтому здесь стоит ровно один взаимоисключающий с полоской случай. */}
      {verdict.surfaceLine && (
        <p className="k-verdict__human" data-testid="verdict-surface">
          {verdict.surfaceLine}
        </p>
      )}

      {/* У «Нет чётких снимков спутника» причина И ЕСТЬ факт, и печатается она ОДИН раз:
          заголовок №6 плюс эта строка — вся карточка. */}
      {verdict.reason && (
        <p className="k-verdict__human" data-testid="verdict-reason">
          {verdict.reason}
        </p>
      )}

      {/* ОГОВОРКА — цена заголовка, и она видна всегда: помощь, а не второй вердикт, поэтому
          текстовым шрифтом, на ступень мельче и НИКОГДА не цветом состояния. */}
      {caveat && (
        <p className="k-verdict__means" data-testid="verdict-caveat">
          <span className="k-verdict__means-lead">{T.verdict.caveat.prefix}</span>
          {caveat}
        </p>
      )}

      {!silent && verdict.secondLine && (
        <p className="k-caption k-verdict__reason" data-testid={SECOND_LINE_TESTID[verdict.kind]}>
          {verdict.secondLine}
        </p>
      )}

      {/* ПРИГЛАШЕНИЕ ВЕРНУТЬСЯ — СВОЙ СЛОТ, и правило одного отрицания им не задето: о том,
          чего нет, сказала причина, и сказала один раз, а это — совет, единственный ответ на
          вопрос, который после причины остаётся: когда смотреть снова. Ждут разного и разные
          сроки (снег сходит неделями, облако — за день), поэтому строка выбрана по коду
          препятствия. Слот отдельный ещё и потому, что строка 4 у молчания запрещена: делать
          из совета «то, что делает тезис утверждением» значило бы вернуть второй голос. */}
      {verdict.comeBack && (
        <p className="k-caption k-verdict__comeback" data-testid="verdict-comeback">
          {verdict.comeBack}
        </p>
      )}

      {hasDisclosure && showNumbers && (
        <details className="k-details">
          <summary>{T.verdict.measuredTitle}</summary>
          {verdict.measured && (
            <p className="k-caption" data-testid="verdict-measured">
              {verdict.measured}
            </p>
          )}
          {/* Характер схода — здесь, а не строкой карточки (drop-lines-off-card): резкость
              читается из числа, которое равно шагу сигнала против его же порога, и место
              этого наблюдения — среди прочего «что видно на снимках». */}
          {verdict.dropLine && (
            <p className="k-caption" data-testid="verdict-drop">
              {verdict.dropLine}
            </p>
          )}
        </details>
      )}

      {showImage && (
        <button
          type="button"
          className="k-btn k-btn--ghost k-btn--block"
          data-testid="verdict-show-image"
          onClick={() => onShowDate?.(showImage)}
        >
          {T.verdict.showImage(formatDayMonth(showImage))}
        </button>
      )}
    </section>
  );
}
